import { useDispatch } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { useFormik, FormikHelpers, getIn } from 'formik'
import * as Yup from 'yup'
import {
  Button,
  Grid,
  TextField,
  MenuItem,
  Stack,
  ToggleButtonGroup,
  ToggleButton,
  Typography,
  List,
  ListItem,
  ListItemIcon,
  ListItemSecondaryAction,
  ListItemText,
  FormControl,
  FormControlLabel,
  Checkbox,
  Alert,
  IconButton,
  ListItemProps,
  Tooltip
} from '@mui/material'
import { CatalogProductType } from '../enums/CatalogProductType'
import { CatalogProductInput } from '../types'
import { createCatalogProduct, updateCatalogProduct } from '../redux/slices/catalog'
import catalogProductService from '../services/catalogProductService'
import { NominalCurrentSelectForSmartElectricalCabinet } from './NominalCurrentSelect'
import PowerSupplyInputCablesSelect from './PowerSupplyInputCablesSelect'
import { CatalogProduct } from '../types'
import CatalogProductManufacturerAutocomplete from './CatalogProductManufacturerAutocomplete'
import CatalogProductProductCodeAutocomplete from './CatalogProductProductCodeAutocomplete'
import {
  isRequired,
  mustBeBoolean,
  mustBeNumber,
  mustBeString,
  mustBeStringOrNull
} from '../utils/validation'
import { createErrorNotification, createSuccessNotification } from '../redux/slices/notifications'
import HeatingPowerAutocomplete from './HeatingPowerAutocomplete'
import { CatalogProductCustomDataInput } from '../types/CatalogProductInput'
import { LoadingButton } from '@mui/lab'
import { usePolicy } from '../hooks'
import { AxiomaW1NominalDiameter, CatalogProductStatus } from '../enums'
import GeneratorApparentPowerField from './GeneratorApparentPowerField'
import GeneratorPowerSourceSelect from './GeneratorPowerSourceSelect'
import PhaseToggleButtonGroup from './PhaseToggleButtonGroup'
import { useEffect, useState } from 'react'
import { Add, Delete } from '@mui/icons-material'
import NominalCurrentSelect from './manufacturing/NominalCurrentSelect'
import ClampRatioField from './manufacturing/ClampRatioField'

enum SpineMultiMeasurementType {
  CUSTOM = 1,
  TWO_400_AND_3_250A = 2,
  FIVE_100A = 3
}
interface CatalogProductFormProps {
  type?: CatalogProductType
  catalogProduct?: CatalogProduct
  onCancel?: () => void
  beforeSubmit?: (values: CatalogProductInput, helpers: FormikHelpers<CatalogProductInput>) => void
  afterSubmit?: (
    catalogProduct: CatalogProduct,
    helpers: FormikHelpers<CatalogProductInput>
  ) => void
}

export default function CatalogProductForm({
  type,
  catalogProduct,
  onCancel,
  beforeSubmit,
  afterSubmit
}: CatalogProductFormProps) {
  /**
   * The translation function.
   */
  const { t } = useTranslation('catalogProduct')

  /**
   * The dispatch function.
   */
  const dispatch = useDispatch()

  /**
   * The policy state.
   */
  const { isSuperadmin, isManufacturer } = usePolicy()

  const [spineMultiMeasurementType, setSpineMultiMeasurementType] =
    useState<SpineMultiMeasurementType>(SpineMultiMeasurementType.CUSTOM)

  const initialValues: CatalogProductInput = {
    type: catalogProduct?.type ?? type ?? CatalogProductType.ELECTRICAL_CABINET,
    name: catalogProduct?.name ?? '',
    description: catalogProduct?.description ?? '',
    manufacturer: catalogProduct?.manufacturer ?? '',
    manufacturerProductCode: catalogProduct?.manufacturerProductCode ?? '',
    status:
      catalogProduct?.status !== undefined
        ? catalogProduct.status
        : isSuperadmin || isManufacturer
          ? CatalogProductStatus.VERIFIED
          : CatalogProductStatus.UNVERIFIED,
    customData: getInitialCustomData()
  }

  /**
   * The validation schema
   */
  const validationSchema = Yup.object().shape({
    name: Yup.string()
      .typeError(mustBeString(t('name')))
      .required(isRequired(t('name'))),
    type: Yup.string()
      .typeError(mustBeString(t('type')))
      .required(isRequired(t('type'))),
    manufacturer: Yup.string()
      .typeError(mustBeString(t('manufacturer')))
      .required(isRequired(t('manufacturer'))),
    manufacturerProductCode: Yup.string()
      .typeError(mustBeStringOrNull(t('manufacturerProductCode')))
      // .required(isRequired(t('manufacturerProductCode')))
      .nullable(),
    customData: Yup.object().shape({
      fuse: Yup.number().when('type', {
        is: (type: CatalogProductType) =>
          type === CatalogProductType.ELECTRICAL_CABINET ||
          type === CatalogProductType.SPINE_MODULE,
        then: Yup.number()
          .typeError(mustBeNumber(t('fuse')))
          .required(isRequired(t('fuse')))
      }),
      inputCables: Yup.number().when('type', {
        is: (type: CatalogProductType) =>
          type === CatalogProductType.ELECTRICAL_CABINET ||
          type === CatalogProductType.SPINE_MODULE,
        then: Yup.number()
          .typeError(mustBeNumber(t('inputCables')))
          .required(isRequired(t('inputCables')))
      }),
      isMainDistributionAssembly: Yup.boolean().when('type', {
        is: (type: CatalogProductType) =>
          type === CatalogProductType.ELECTRICAL_CABINET ||
          type === CatalogProductType.SPINE_MODULE,
        then: Yup.boolean()
          .typeError(mustBeBoolean(t('isMainDistributionAssembly')))
          .required(isRequired(t('isMainDistributionAssembly')))
      }),
      heatingPower: Yup.number().when('type', {
        is: (type: CatalogProductType) => type === CatalogProductType.HEATER,
        then: Yup.number()
          .typeError(mustBeNumber(t('heatingPower')))
          .required(isRequired(t('heatingPower')))
      }),
      nominalDiameter: Yup.number().when('type', {
        is: (type: CatalogProductType) => type === CatalogProductType.AXIOMA_W1,
        then: Yup.number()
          .typeError(mustBeNumber(t('nominalDiameter')))
          .required(isRequired(t('nominalDiameter')))
          .oneOf(
            Object.values(AxiomaW1NominalDiameter).filter(
              (diameter) => typeof diameter === 'number'
            )
          )
      })
    })
  })

  function getInitialCustomData(): CatalogProductCustomDataInput {
    if (catalogProduct !== undefined) {
      return catalogProduct.customData ?? {}
    } else if (type === CatalogProductType.AXIOMA_W1) {
      return {
        nominalDiameter: AxiomaW1NominalDiameter.DN25
      }
    } else if (type === CatalogProductType.HEATER) {
      return { heatingPower: 0 }
    } else if (type === CatalogProductType.SPINE_MULTI_MEASUREMENT) {
      return {
        outputs: {}
      }
    } else {
      return {
        fuse: 0,
        isMainDistributionAssembly: false,
        inputCables: 0
      }
    }
  }

  async function onSubmit(
    values: CatalogProductInput,
    { setSubmitting, ...helpers }: FormikHelpers<CatalogProductInput>
  ): Promise<void> {
    try {
      setSubmitting(true)

      beforeSubmit?.(values, { setSubmitting, ...helpers })

      if (catalogProduct === undefined) {
        const createdCatalogProduct = await catalogProductService.createCatalogProduct(values)

        dispatch(createCatalogProduct(createdCatalogProduct))
        dispatch(createSuccessNotification(t('catalogProductCreateSuccess')))

        afterSubmit?.(createdCatalogProduct, { setSubmitting, ...helpers })
      } else {
        const updatedCatalogProduct = await catalogProductService.updateCatalogProduct(
          catalogProduct.id,
          values
        )

        dispatch(updateCatalogProduct(updatedCatalogProduct))
        dispatch(createSuccessNotification(t('catalogProductUpdateSuccess')))

        afterSubmit?.(updatedCatalogProduct, { setSubmitting, ...helpers })
      }
    } catch (_) {
      if (catalogProduct === undefined) {
        dispatch(createErrorNotification(t('createCatalogProductError')))
      } else {
        dispatch(createErrorNotification(t('updateCatalogProductError')))
      }
    } finally {
      setSubmitting(false)
    }
  }

  const {
    values,
    errors,
    touched,
    isSubmitting,
    setFieldValue,
    setFieldTouched,
    handleBlur,
    handleChange,
    handleSubmit
  } = useFormik({
    initialValues,
    validationSchema,
    onSubmit
  })

  function handleCancel(): void {
    onCancel?.()
  }

  function isError(field: string): boolean {
    return Boolean(getIn(touched, field) && getIn(errors, field))
  }

  function getHelperText(field: string): string | undefined {
    return getIn(touched, field) ? getIn(errors, field) : undefined
  }

  useEffect(() => {
    if (catalogProduct !== undefined) {
      setFieldValue('customData', catalogProduct.customData ?? {})
    } else {
      switch (values.type) {
        case CatalogProductType.HEATER:
          setFieldValue('customData', { heatingPower: 0 })
          break
        case CatalogProductType.SPINE_MULTI_MEASUREMENT:
          setFieldValue('customData', {
            shipsAsInstallationKit: false,
            outputs: {
              '1': {
                nominalCurrent: 100,
                clampRatio: 1000
              }
            }
          })
          break
        case CatalogProductType.AXIOMA_W1:
          setFieldValue('customData', { nominalDiameter: AxiomaW1NominalDiameter.DN25 })
          break
        case CatalogProductType.AURORA:
          setFieldValue('customData', { heatingPower: 0 })
          break
        default:
          setFieldValue('customData', {
            fuse: 0,
            isMainDistributionAssembly: false,
            inputCables: 0
          })
      }
    }
  }, [values.type])

  return (
    <form onSubmit={handleSubmit}>
      <Grid container spacing={6}>
        {type === undefined && (
          <Grid item xs={12}>
            <TextField
              select
              name="type"
              label={t('type')}
              value={values.type}
              error={Boolean(touched.type && errors.type)}
              helperText={touched.type && errors.type}
              variant="outlined"
              fullWidth
              onBlur={handleBlur}
              onChange={handleChange}
            >
              <MenuItem value={CatalogProductType.ELECTRICAL_CABINET}>
                {t('electricalCabinet')}
              </MenuItem>
              <MenuItem value={CatalogProductType.SPINE_MODULE}>{t('spineModule')}</MenuItem>
              <MenuItem value={CatalogProductType.SPINE_MULTI_MEASUREMENT}>
                {t('spineMultiMeasurement')}
              </MenuItem>
              <MenuItem value={CatalogProductType.RUUVI_TAG}>{t('ruuviTag')}</MenuItem>
              <MenuItem value={CatalogProductType.HEATER}>{t('catalogProduct:heater')}</MenuItem>
              <MenuItem value={CatalogProductType.GENERATOR}>
                {t('catalogProduct:generator')}
              </MenuItem>
              <MenuItem value={CatalogProductType.AURORA}>{t('catalogProduct:aurora')}</MenuItem>
              <MenuItem value={CatalogProductType.AXIOMA_W1}>
                {t('catalogProduct:axiomaW1')}
              </MenuItem>
            </TextField>
          </Grid>
        )}

        <Grid item xs={12}>
          <TextField
            name="name"
            label={t('name')}
            value={values.name}
            error={Boolean(touched.name && errors.name)}
            helperText={touched.name && errors.name}
            variant="outlined"
            fullWidth
            onBlur={handleBlur}
            onChange={handleChange}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          {values.type === CatalogProductType.HEATER ? (
            <CatalogProductManufacturerAutocomplete
              query={{
                type: CatalogProductType.HEATER,
                status: CatalogProductStatus.VERIFIED
              }}
              value={values.manufacturer}
              label={t('manufacturer')}
              error={Boolean(touched.manufacturer && errors.manufacturer)}
              helperText={touched.manufacturer && errors.manufacturer}
              fullWidth
              onBlur={handleBlur}
              onChange={(_, selectedValue) => {
                setFieldValue('manufacturer', selectedValue ?? '')
                setFieldTouched('manufacturer', true, false)
              }}
              onInputChange={(_, inputValue) => {
                setFieldValue('manufacturer', inputValue)
                setFieldTouched('manufacturer', true, false)
              }}
            />
          ) : (
            <TextField
              name="manufacturer"
              label={t('manufacturer')}
              value={values.manufacturer}
              error={Boolean(touched.manufacturer && errors.manufacturer)}
              helperText={touched.manufacturer && errors.manufacturer}
              fullWidth
              onBlur={handleBlur}
              onChange={handleChange}
            />
          )}
        </Grid>

        <Grid item xs={12} md={6}>
          {values.type === CatalogProductType.HEATER ? (
            <CatalogProductProductCodeAutocomplete
              query={{
                type: CatalogProductType.HEATER,
                status: CatalogProductStatus.VERIFIED
              }}
              value={values.manufacturerProductCode}
              label={t('manufacturerProductCode')}
              error={Boolean(touched.manufacturerProductCode && errors.manufacturerProductCode)}
              helperText={touched.manufacturerProductCode && errors.manufacturerProductCode}
              fullWidth
              onBlur={handleBlur}
              onChange={(_, selectedValue) => {
                setFieldValue('manufacturerProductCode', selectedValue ?? '')
                setFieldTouched('manufacturerProductCode', true, false)
              }}
              onInputChange={(_, inputValue) => {
                setFieldValue('manufacturerProductCode', inputValue)
                setFieldTouched('manufacturerProductCode', true, false)
              }}
            />
          ) : (
            <TextField
              name="manufacturerProductCode"
              label={t('manufacturerProductCode')}
              value={values.manufacturerProductCode}
              error={Boolean(touched.manufacturerProductCode && errors.manufacturerProductCode)}
              helperText={touched.manufacturerProductCode && errors.manufacturerProductCode}
              fullWidth
              onBlur={handleBlur}
              onChange={handleChange}
            />
          )}
        </Grid>

        <Grid item xs={12}>
          <TextField
            multiline
            rows={4}
            name="description"
            label={t('description')}
            value={values.description}
            error={Boolean(touched.description && errors.description)}
            helperText={touched.description && errors.description}
            fullWidth
            onBlur={handleBlur}
            onChange={handleChange}
          />
        </Grid>

        {(values.type === CatalogProductType.ELECTRICAL_CABINET ||
          values.type === CatalogProductType.SPINE_MODULE) && (
          <>
            <Grid item xs={12} md={4}>
              <NominalCurrentSelectForSmartElectricalCabinet
                name="customData.fuse"
                value={values.customData.fuse}
                error={Boolean(touched?.customData?.fuse && errors?.customData?.fuse)}
                helperText={touched?.customData?.fuse && errors?.customData?.fuse}
                fullWidth
                onBlur={handleBlur}
                onChange={handleChange}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <PowerSupplyInputCablesSelect
                name="customData.inputCables"
                label={t('PowerSupply.inputCables')}
                value={values.customData.inputCables}
                error={Boolean(touched?.customData?.inputCables && errors?.customData?.inputCables)}
                helperText={touched?.customData?.inputCables && errors?.customData?.inputCables}
                fullWidth
                onChange={handleChange}
                onBlur={handleBlur}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                select
                name="customData.isMainDistributionAssembly"
                label={t('isMainDistributionAssembly')}
                value={values.customData.isMainDistributionAssembly}
                error={Boolean(
                  touched?.customData?.isMainDistributionAssembly &&
                  errors?.customData?.isMainDistributionAssembly
                )}
                helperText={
                  touched?.customData?.isMainDistributionAssembly &&
                  errors?.customData?.isMainDistributionAssembly
                }
                variant="outlined"
                fullWidth
                onBlur={handleBlur}
                onChange={handleChange}
              >
                {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
                <MenuItem value={false as any}>{t('no')}</MenuItem>
                {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
                <MenuItem value={true as any}>{t('yes')}</MenuItem>
              </TextField>
            </Grid>
          </>
        )}

        {values.type === CatalogProductType.HEATER && (
          <Grid item xs={12}>
            <HeatingPowerAutocomplete
              name="customData.heatingPower"
              label={t('heater:heatingPower')}
              value={values.customData.heatingPower}
              error={Boolean(touched?.customData?.heatingPower && errors?.customData?.heatingPower)}
              helperText={touched?.customData?.heatingPower && errors?.customData?.heatingPower}
              fullWidth
              onBlur={handleBlur}
              onChange={(value) => {
                setFieldValue('customData.heatingPower', value)
                setFieldTouched('customData.heatingPower', true)
              }}
            />
          </Grid>
        )}

        {values.type === CatalogProductType.GENERATOR && (
          <>
            <Grid item xs={12}>
              <GeneratorPowerSourceSelect
                name="customData.powerSource"
                label={t('powerSource')}
                value={values.customData.powerSource}
                disabled={isSubmitting}
                error={isError('customData.powerSource')}
                helperText={getHelperText('customData.powerSource')}
                variant="outlined"
                fullWidth
                onBlur={handleBlur}
                onChange={handleChange}
              />
            </Grid>

            <Grid item xs={12}>
              <PhaseToggleButtonGroup
                fullWidth
                size="small"
                value={
                  values.customData.nominalVoltage === undefined ||
                  values.customData.nominalVoltage <= 230
                    ? 1
                    : 3
                }
                disabled={isSubmitting}
                onChange={(_event, value) => {
                  if (value === 1) {
                    setFieldValue('customData.nominalVoltage', 230)
                  } else {
                    setFieldValue('customData.nominalVoltage', 400)
                  }
                }}
              />
            </Grid>

            <Grid item xs={12}>
              <GeneratorApparentPowerField
                name="customData.apparentPower"
                value={values.customData.apparentPower}
                disabled={isSubmitting}
                error={isError('customData.apparentPower')}
                helperText={getHelperText('customData.apparentPower')}
                onBlur={handleBlur}
                onChange={handleChange}
              />
            </Grid>
          </>
        )}

        {values.type === CatalogProductType.AURORA && (
          <Grid item xs={12}>
            <TextField
              name="customData.heatingPower"
              label={`${t('aurora:heatingPower')} (kW)`}
              value={values.customData.heatingPower || ''}
              error={isError('customData.heatingPower')}
              helperText={getHelperText('customData.heatingPower')}
              variant="outlined"
              fullWidth
              onBlur={handleBlur}
              onChange={handleChange}
            />
          </Grid>
        )}

        {values.type === CatalogProductType.AXIOMA_W1 && (
          <Grid item xs={12}>
            <TextField
              name="customData.nominalDiameter"
              label={`${t('axioma:nominalDiameter')} (mm)`}
              value={values.customData.nominalDiameter ?? AxiomaW1NominalDiameter.DN25}
              error={isError('customData.nominalDiameter')}
              helperText={getHelperText('customData.nominalDiameter')}
              variant="outlined"
              fullWidth
              select
              onBlur={handleBlur}
              onChange={handleChange}
            >
              {Object.values(AxiomaW1NominalDiameter)
                .filter((diameter) => typeof diameter === 'number')
                .map((diameter) => (
                  <MenuItem key={diameter} value={diameter}>
                    {diameter}
                  </MenuItem>
                ))}
            </TextField>
          </Grid>
        )}

        {values.type === CatalogProductType.SPINE_MULTI_MEASUREMENT && (
          <Grid item xs={12}>
            {/*<ToggleButtonGroup
              value={spineMultiMeasurementType}
              exclusive
              fullWidth
              color="primary"
              onChange={(_event, value) => setSpineMultiMeasurementType(value)}
            >
              <ToggleButton
                value={SpineMultiMeasurementType.FIVE_100A}
                onClick={() => {
                  setFieldValue('customData.outputs', {
                    1: { nominalCurrent: 100, clampRatio: 1000 },
                    2: { nominalCurrent: 100, clampRatio: 1000 },
                    3: { nominalCurrent: 100, clampRatio: 1000 },
                    4: { nominalCurrent: 100, clampRatio: 1000 },
                    5: { nominalCurrent: 100, clampRatio: 1000 }
                  })
                }}
              >
                5 x 100 A
              </ToggleButton>

              <ToggleButton
                value={SpineMultiMeasurementType.TWO_400_AND_3_250A}
                onClick={() => {
                  setFieldValue('customData.outputs', {
                    1: { nominalCurrent: 400, clampRatio: 4000 },
                    2: { nominalCurrent: 400, clampRatio: 4000 },
                    3: { nominalCurrent: 250, clampRatio: 2500 },
                    4: { nominalCurrent: 250, clampRatio: 2500 },
                    5: { nominalCurrent: 250, clampRatio: 2500 }
                  })
                }}
              >
                2 x 400 A + 3 x 250 A
              </ToggleButton>

              <ToggleButton
                value={SpineMultiMeasurementType.CUSTOM}
                onClick={() => {
                  setFieldValue('customData.outputs', {
                    1: {},
                    2: {},
                    3: {},
                    4: {},
                    5: {}
                  })
                }}
              >
                {t('custom')}
              </ToggleButton>
            </ToggleButtonGroup>*/}

            <OutputsManager
              value={values.customData.outputs}
              onChange={(outputs) => setFieldValue('customData.outputs', outputs)}
            />

            <FormControl fullWidth sx={{ mt: 4 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={values.customData?.shipAsInstallationKit}
                    onChange={(_, checked) =>
                      setFieldValue('customData.shipAsInstallationKit', checked)
                    }
                  />
                }
                label={t('shipsAsInstallationKit')}
              />
            </FormControl>
          </Grid>
        )}

        {(isSuperadmin || isManufacturer) && (
          <Grid item xs={12}>
            <TextField
              select
              name="status"
              label={t('status')}
              value={values.status}
              error={Boolean(touched.status && errors.status)}
              helperText={touched.status && errors.status}
              variant="outlined"
              fullWidth
              onBlur={handleBlur}
              onChange={handleChange}
            >
              <MenuItem value={CatalogProductStatus.UNVERIFIED}>
                {t('manufacturing:CatalogProductStatus.unverified')}
              </MenuItem>
              <MenuItem value={CatalogProductStatus.VERIFIED}>
                {t('manufacturing:CatalogProductStatus.verified')}
              </MenuItem>
            </TextField>
          </Grid>
        )}

        <Grid item xs={12}>
          <Stack direction="row" justifyContent="flex-end" alignItems="center" spacing={3}>
            <Button variant="text" color="inherit" onClick={handleCancel}>
              {t('actions:cancel')}
            </Button>

            <LoadingButton type="submit" variant="contained" color="primary" loading={isSubmitting}>
              {t('actions:save')}
            </LoadingButton>
          </Stack>
        </Grid>
      </Grid>
    </form>
  )
}

interface OutputsManagerProps {
  value: CatalogProductCustomDataInput['outputs']
  onChange: (value: CatalogProductCustomDataInput['outputs']) => void
}

function OutputsManager({ value, onChange }: OutputsManagerProps) {
  const { t } = useTranslation('catalogProduct')

  const addOutput = (): void => {
    if (value) {
      const outputs = value
      const nextOutputNumber = Object.keys(outputs).length + 1
      onChange({
        ...value,
        [nextOutputNumber]: {
          nominalCurrent: 100,
          clampRatio: 1000
        }
      })
    } else {
      onChange({ 1: {} })
    }
  }

  const handleRemove = (index: string) => {
    if (value) {
      const outputs = value
      delete outputs[index]

      // Remap indexed from 1 to n without gaps
      const remappedOutputs: CatalogProductCustomDataInput['outputs'] = {}
      Object.keys(outputs).forEach((key, i) => {
        remappedOutputs[i + 1] = outputs[key]
      })

      onChange(remappedOutputs)
    }
  }

  const handleChange = (index: string, outputValue: CurrentTransformers) => {
    const outputs = value !== undefined ? value : {}
    onChange({
      ...outputs,
      [index]: outputValue
    })
  }

  const handleNominalCurrentChange = (index: string, nominalCurrent: number) => {
    const outputs = value !== undefined ? value : {}
    const output = outputs[index] ?? {}
    onChange({
      ...outputs,
      [index]: {
        ...output,
        nominalCurrent
      }
    })
  }

  const handleClampRatioChange = (index: string, clampRatio: number) => {
    const outputs = value !== undefined ? value : {}
    const output = outputs[index] ?? {}
    onChange({
      ...outputs,
      [index]: {
        ...output,
        clampRatio
      }
    })
  }

  if (value === undefined) {
    return null
  }

  const outputIndexes = Object.keys(value)

  return (
    <>
      <Typography color="text.secondary" sx={{ mb: 2 }}>
        {t('outputs')}
      </Typography>
      <List>
        {outputIndexes.length === 0 ? (
          <Alert severity="warning">{t('noOutputs')}</Alert>
        ) : (
          <>
            {outputIndexes.map((index) => (
              <OutputListItem
                key={index}
                index={index}
                value={value[index]}
                onValueChange={(outputValue) => handleChange(index, outputValue)}
                secondaryAction={
                  <>
                    {outputIndexes.length > 1 && (
                      <Tooltip title={t('actions:remove')}>
                        <IconButton color="error" edge="end" onClick={() => handleRemove(index)}>
                          <Delete />
                        </IconButton>
                      </Tooltip>
                    )}
                  </>
                }
              />
            ))}
          </>
        )}
      </List>

      <Button startIcon={<Add />} color="primary" variant="outlined" onClick={addOutput}>
        {t('addOutput')}
      </Button>
    </>
  )
}

type CurrentTransformers = {
  nominalCurrent?: number
  clampRatio?: number
}

interface OutputListItemProps extends Omit<ListItemProps, 'value' | 'children'> {
  index: string
  value: CurrentTransformers
  onValueChange: (value: CurrentTransformers) => void
}

function OutputListItem({ index, value, onValueChange, ...props }: OutputListItemProps) {
  const { t } = useTranslation('catalogProduct')

  const handleNominalCurrentChange = (nominalCurrent: number) => {
    onValueChange({
      ...value,
      nominalCurrent
    })
  }

  const handleClampRatioChange = (clampRatio: number | null) => {
    if (clampRatio === null) {
      onValueChange({
        ...value,
        clampRatio: undefined
      })
    } else {
      onValueChange({
        ...value,
        clampRatio
      })
    }
  }

  useEffect(() => {
    if (value.nominalCurrent === undefined) {
      return
    }

    if (value.nominalCurrent === 400) {
      onValueChange({
        ...value,
        clampRatio: 4000
      })
    } else if (value.nominalCurrent === 600) {
      onValueChange({
        ...value,
        clampRatio: 6000
      })
    } else if (value.nominalCurrent <= 100) {
      onValueChange({
        ...value,
        clampRatio: 1000
      })
    } else {
      onValueChange({
        ...value,
        clampRatio: 2500
      })
    }
  }, [value?.nominalCurrent])

  return (
    <ListItem {...props}>
      <ListItemIcon sx={{ minWidth: '32px' }}>
        <Typography variant="h6">{index}</Typography>
      </ListItemIcon>
      <ListItemText
        primary={
          <Grid container spacing={2}>
            <Grid item xs={4}>
              <NominalCurrentSelect
                value={value?.nominalCurrent !== undefined ? value.nominalCurrent : null}
                onValueChange={handleNominalCurrentChange}
              />
            </Grid>
            <Grid item xs={4}>
              <ClampRatioField
                value={value?.clampRatio !== undefined ? value.clampRatio : null}
                onValueChange={handleClampRatioChange}
              />
            </Grid>
          </Grid>
        }
      />
    </ListItem>
  )
}
